import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import differenceInYears from "date-fns/differenceInYears";
import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { PatientProfile } from "src/api/types";
import { theme } from "src/assets/theme";
import MagicLinkButton from "src/shared/MagicLinkButton";
import MasonryColumnsSelector from "../MasonryLayout/components/MasonryColumnsSelector";
import { useGetPatientSubscriptionsByIdQuery } from "src/api/services/subscriptionsApi";
import { isNonProviderView } from "src/utils/isNonProviderView";
import { useClinician } from "src/context/SessionProvider/ClinicianProvider";
import Box from "@mui/material/Box";

interface Props {
  patient: PatientProfile;
}

const PatientDemographics = ({ patient }: Props) => {
  const roles = useClinician((c) => c.roles);
  const { data: subscriptions } = useGetPatientSubscriptionsByIdQuery(
    patient.id,
  );

  const canQualityReview = useMemo(() => {
    return roles.some((r) => ["Developer", "Operations"].includes(r.name));
  }, [roles]);

  const { pathname } = useLocation();

  const isNPV = useMemo(() => isNonProviderView(pathname), [pathname]);

  const isUnpaid = useMemo(() => {
    if (subscriptions == null) return false;
    return subscriptions.some((s) => s.status === "unpaid");
  }, [subscriptions]);

  const patientInfo = useMemo(() => {
    const age = differenceInYears(
      new Date(),
      new Date(patient?.profile.dob || ""),
    );

    return `${patient?.profile.first_name} ${patient?.profile.last_name}, ${
      age || null
    }, PID: ${patient?.id}`;
  }, [patient]);

  return (
    <Stack direction="row" justifyContent="space-between" width={"100%"}>
      <Stack direction="row" gap={2}>
        <Typography variant="h4">{patientInfo}</Typography>
        {isUnpaid === true ? (
          <Box
            sx={{
              display: "flex",
              backgroundColor: theme.palette.secondary.main,
              padding: "0px 16px",
              justifyContent: "center",
              alignItems: "center",
              borderRadius: "6px",
              height: "40px",
            }}
          >
            <Typography color="#ffffff" fontWeight={700}>
              UNPAID INVOICE
            </Typography>
          </Box>
        ) : null}
      </Stack>
      <Stack direction="row" gap={2}>
        {canQualityReview && isNPV ? (
          <MagicLinkButton
            patientEmail={patient.profile.email}
            buttonText="Quality Review"
          />
        ) : null}
        <MasonryColumnsSelector />
      </Stack>
    </Stack>
  );
};

export default PatientDemographics;
